// Example Dashboard Component
// Demonstrates CRUD operations with Firestore
// Generated by @firebase-setup agent

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useFirestore } from '../hooks/useFirestore';
import { useCollection } from '../hooks/useCollection';
import { where, orderBy } from 'firebase/firestore';

export const Dashboard = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Firestore hook for CRUD operations
  const {
    createDocument,
    updateDocument,
    deleteDocument,
    loading: actionLoading
  } = useFirestore('items');

  // Real-time collection subscription - only user's items
  // Note: orderBy removed temporarily - items will be in random order
  // Create Firestore index to enable sorting by createdAt
  const { data: items, loading: itemsLoading } = useCollection('items', [
    where('userId', '==', currentUser?.uid || '')
    // orderBy('createdAt', 'desc') // Commented out - requires Firestore index
  ]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!title.trim()) {
      setMessage('Title is required');
      return;
    }

    if (editingId) {
      // Update existing item
      const result = await updateDocument(editingId, {
        title: title.trim(),
        description: description.trim()
      });

      if (result.success) {
        setMessage('Item updated successfully!');
        setEditingId(null);
      } else {
        setMessage('Error updating item: ' + result.error);
      }
    } else {
      // Create new item
      const result = await createDocument({
        title: title.trim(),
        description: description.trim()
      });

      if (result.success) {
        setMessage('Item created successfully!');
      } else {
        setMessage('Error creating item: ' + result.error);
      }
    }

    setTitle('');
    setDescription('');
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setTitle(item.title);
    setDescription(item.description || '');
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    const result = await deleteDocument(itemId);

    if (result.success) {
      setMessage('Item deleted successfully!');
    } else {
      setMessage('Error deleting item: ' + result.error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setTitle('');
    setDescription('');
  };

  return (
    <div style={{ maxWidth: '800px', margin: '50px auto', padding: '20px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h2>Dashboard</h2>
          <p style={{ color: '#666' }}>Welcome, {currentUser?.displayName || currentUser?.email}!</p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Logout
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: message.includes('Error') ? '#fee' : '#efe',
          color: message.includes('Error') ? '#c00' : '#080',
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}

      {/* Create/Edit Form */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <h3>{editingId ? 'Edit Item' : 'Create New Item'}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="title" style={{ display: 'block', marginBottom: '5px' }}>
              Title *
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '5px' }}>
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              disabled={actionLoading}
              style={{
                padding: '10px 20px',
                backgroundColor: editingId ? '#ffc107' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: actionLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {actionLoading ? 'Saving...' : (editingId ? 'Update Item' : 'Create Item')}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Items List */}
      <div>
        <h3>Your Items ({items.length})</h3>

        {itemsLoading ? (
          <p>Loading items...</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            No items yet. Create your first item above!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {items.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: '15px',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start'
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>{item.title}</h4>
                    {item.description && (
                      <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                        {item.description}
                      </p>
                    )}
                    <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>
                      Created: {new Date(item.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginLeft: '15px' }}>
                    <button
                      onClick={() => handleEdit(item)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      disabled={actionLoading}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
